import { useEffect, useRef } from 'react'

/**
 * The document, with a box drawn around whichever field is selected.
 *
 * This is what makes verification fast enough to actually do. Without it,
 * checking an amount means reading the invoice yourself and finding the number
 * — with it, you glance at where the highlight landed and you are done. It is
 * also the honest answer to "where did this figure come from?".
 *
 * Bounding boxes arrive normalised 0–1 (the pipeline divides by page size), so
 * they map to percentages here and stay correct at any display width.
 */
export function ProvenanceImage({
  src,
  bbox,
  onOpenFull,
}: {
  src: string | null
  bbox: [number, number, number, number] | null
  onOpenFull?: () => void
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const boxRef = useRef<HTMLDivElement>(null)

  // Bring the highlighted region into view when the selection changes.
  useEffect(() => {
    if (!bbox || !boxRef.current || !scrollRef.current) return
    const container = scrollRef.current
    const box = boxRef.current
    const target = box.offsetTop - container.clientHeight / 2 + box.clientHeight / 2
    container.scrollTo({ top: Math.max(0, target), behavior: 'smooth' })
  }, [bbox])

  if (!src) {
    return (
      <div className="h-full grid place-items-center bg-line/40">
        <p className="text-sm text-muted">Geen afbeelding</p>
      </div>
    )
  }

  return (
    <div ref={scrollRef} className="h-full overflow-y-auto bg-black/90 scroll-area">
      <div className="relative">
        <img src={src} alt="Document" className="block w-full h-auto" />

        {bbox && (
          <div
            ref={boxRef}
            className="absolute border-2 border-warn rounded-sm pointer-events-none
                       shadow-[0_0_0_9999px_rgba(0,0,0,0.45)] transition-all duration-200"
            style={{
              left: `${bbox[0] * 100}%`,
              top: `${bbox[1] * 100}%`,
              width: `${bbox[2] * 100}%`,
              height: `${bbox[3] * 100}%`,
            }}
          />
        )}

        {onOpenFull && (
          <button
            onClick={onOpenFull}
            className="absolute top-2 right-2 px-3 py-1.5 rounded-full bg-black/60 text-white text-xs"
          >
            Volledig scherm
          </button>
        )}
      </div>
    </div>
  )
}
